import * as ProgressPrimitive from "@radix-ui/react-progress"
import { cva, type VariantProps } from "class-variance-authority"
import * as React from "react"
import { cn } from "../utils"

type ProgressVariant = "bar" | "circle"

const progressCircleVariants = cva("", {
  variants: {
    color: {
      primary: "text-primary",
      gradient: "text-gradient-primary",
      destructive: "text-destructive",
    },
  },
  defaultVariants: {
    color: "primary",
  },
})

const progressBarVariants = cva("h-full w-full flex-1 transition-all rounded-r-full", {
  variants: {
    color: {
      primary: "bg-primary",
      gradient: "bg-gradient-primary",
      destructive: "bg-destructive",
    },
  },
  defaultVariants: {
    color: "primary",
  },
})

type ProgressProps = React.ComponentPropsWithoutRef<typeof ProgressPrimitive.Root> &
  VariantProps<typeof progressCircleVariants> & {
    innerClassName?: string
    variant?: ProgressVariant
    size?: number
    width?: number
  }

const Progress = React.forwardRef<React.ElementRef<typeof ProgressPrimitive.Root>, ProgressProps>(
  (
    {
      className,
      innerClassName,
      value = 0,
      max = 100,
      variant = "bar",
      size = 40,
      color,
      width,
      ...props
    },
    ref,
  ) => {
    // Calculate real percentage first, ensuring it's between 0 and 100
    const realPercentage = Math.min(100, Math.max(0, ((value ?? 0) / max) * 100))

    if (variant === "circle") {
      const radius = (size - 10) / 2 // Account for stroke width
      const circumference = 2 * Math.PI * radius
      // Use 4% minimum for circle
      const displayPercentage = realPercentage <= 4 ? 4 : realPercentage
      // Progress goes from circumference (empty) to 0 (full), using displayPercentage for minimum 4% visibility
      const strokeDashoffset = (1 - displayPercentage / 100) * circumference

      const getStrokeColor = () => {
        if (color === "gradient") return "url(#gradient-primary)"
        return "currentColor"
      }

      return (
        <div className={cn("flex items-center justify-center", className)}>
          <svg
            width="100%"
            height="100%"
            viewBox={`0 0 ${size} ${size}`}
            className="transform -rotate-90 origin-center"
            style={{
              width: size,
              height: size,
            }}
          >
            <defs>
              <radialGradient id="gradient-primary">
                <stop offset="10%" stopColor="var(--chart-1)" />
                <stop offset="95%" stopColor="var(--chart-3)" />
              </radialGradient>
            </defs>
            {/* Background circle */}
            <circle
              cx={size / 2}
              cy={size / 2}
              r={radius}
              stroke="currentColor"
              strokeWidth={width ?? size / 10}
              fill="none"
              className="text-muted"
            />
            {/* Progress circle */}
            <circle
              cx={size / 2}
              cy={size / 2}
              r={radius}
              stroke={getStrokeColor()}
              strokeWidth={width ?? size / 10}
              fill="none"
              style={{
                strokeDasharray: `${circumference}`,
                strokeDashoffset: `${strokeDashoffset}`,
                transition: "stroke-dashoffset 0.3s ease-in-out",
              }}
              strokeLinecap="butt"
              className={cn(
                "text-primary transition-all duration-300",
                color === "destructive" && "text-destructive",
                color === "gradient" && "text-gradient-primary",
                innerClassName,
              )}
            />
          </svg>
        </div>
      )
    }

    // Use 2% minimum for bar
    const displayPercentage = realPercentage <= 2 ? 2 : realPercentage

    return (
      <ProgressPrimitive.Root
        ref={ref}
        className={cn("relative h-2.5 w-full overflow-hidden rounded-full bg-secondary", className)}
        max={100}
        {...props}
      >
        <ProgressPrimitive.Indicator
          className={cn(progressBarVariants({ color }), innerClassName)}
          style={{ transform: `translateX(-${100 - displayPercentage}%)` }}
        />
      </ProgressPrimitive.Root>
    )
  },
)
Progress.displayName = ProgressPrimitive.Root.displayName

export { Progress }
export type { ProgressVariant }
